import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Trophy, Spade, LogOut, Home, Settings, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { App } from '@capacitor/app';

import { useAuth } from './shared/contexts/AuthContext';
import { LeagueProvider } from './apps/leaderboard/contexts/LeagueContext';

import { Logo } from './shared/components/Logo';
import { LoadingScreen } from './shared/components/LoadingScreen';
import { AuthScreen } from './shared/components/AuthScreen';
import { UpdatePrompt } from './shared/components/UpdatePrompt';
import { LogoutConfirm } from './shared/components/LogoutConfirm';
import { InstallPrompt } from './shared/components/InstallPrompt';
import { isPrivateBuild, isPublicWeb } from './shared/utils/platform';
import { 
  hasActivePokerSession, 
  ACTIVE_APP_STORAGE_KEY,
  JOIN_CODE_QUERY_PARAM,
  setPendingJoinCode,
  extractJoinCodeFromUrl
} from './shared/utils/pokerSession';

// Lazy load apps for better initial performance
const LeaderboardApp = lazy(() => import('./apps/leaderboard/App'));
type PokerAppComponent = typeof import('./apps/poker/PokerApp').PokerAppWithProviders;

let pokerAppModulePromise: Promise<{ default: PokerAppComponent }> | null = null;
const loadPokerApp = () => {
  pokerAppModulePromise ??= import('./apps/poker/PokerApp').then(m => ({ default: m.PokerAppWithProviders }));
  return pokerAppModulePromise;
};

const PokerApp = lazy(loadPokerApp);

function ViewingHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-900 z-50">
      <div className="max-w-md mx-auto p-4 flex items-center gap-4">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white transition-all active:scale-90"
        >
          <Home size={18} />
        </button>
        <div className="flex-1 flex items-center gap-3">
          <div>
            <h1 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 leading-none">Viewing</h1>
            <p className="text-sm font-bold text-white uppercase tracking-tight mt-0.5">{title}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppSwitch() {
  const { user, loading, logout } = useAuth();
  const [currentApp, setCurrentApp] = useState<'home' | 'leaderboard' | 'poker'>(() => {
    if (import.meta.env.VITE_PUBLIC_OFFLINE === 'true') return 'poker';
    if (hasActivePokerSession()) return 'poker';
    try {
      const saved = localStorage.getItem(ACTIVE_APP_STORAGE_KEY);
      if (saved === 'poker' || saved === 'leaderboard' || saved === 'home') {
        return saved;
      }
    } catch {}
    return 'home';
  });
  const [isPokerStartupReady, setIsPokerStartupReady] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showHeader, setShowHeader] = useState(() => {
    if (hasActivePokerSession()) {
      return false;
    }
    return true;
  });

  const openPokerApp = () => {
    setCurrentApp('poker');
  };

  const isStartupLoading = loading || (Boolean(user) && currentApp === 'poker' && !isPokerStartupReady);
  const startupLoadingScreen = isStartupLoading ? <LoadingScreen variant="rose" /> : null;

  // Handle direct join links
  useEffect(() => {
    const setJoinTarget = (code: string) => {
      openPokerApp();
      setPendingJoinCode(code);
      window.dispatchEvent(new CustomEvent('poker-join-code-received', { detail: code }));
    };

    // 1. Handle URL params on initial browser load
    const initialJoinCode = extractJoinCodeFromUrl(window.location.href);
    if (initialJoinCode) {
      setJoinTarget(initialJoinCode);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete(JOIN_CODE_QUERY_PARAM);
      window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }

    // 2. Handle cold-start deep links from native app launch
    App.getLaunchUrl().then((launchData) => {
      const launchJoinCode = extractJoinCodeFromUrl(launchData?.url);
      if (launchJoinCode) {
        setJoinTarget(launchJoinCode);
      }
    }).catch((e) => {
      console.error('Failed to read launch URL:', e);
    });

    // 3. Handle deep links while app is running (Capacitor)
    const sub = App.addListener('appUrlOpen', data => {
      const code = extractJoinCodeFromUrl(data.url);
      if (code) {
        setJoinTarget(code);
      }
    });

    return () => {
      sub.then(s => s.remove());
    };
  }, []);

  // Persist currentApp selection (unless in public offline mode)
  useEffect(() => {
    if (import.meta.env.VITE_PUBLIC_OFFLINE !== 'true') {
      try {
        localStorage.setItem(ACTIVE_APP_STORAGE_KEY, currentApp);
      } catch {}
    }
  }, [currentApp]);

  // Check if authenticated user has an active poker session to restore
  useEffect(() => {
    if (!loading && user && currentApp !== 'poker' && !isPublicWeb()) {
      if (hasActivePokerSession(user.uid)) {
        openPokerApp();
      }
    }
  }, [loading, user, currentApp]);

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      localStorage.removeItem(ACTIVE_APP_STORAGE_KEY);
    } catch {}
    setCurrentApp('home');
    await logout();
  };

  // Reset header when switching apps
  useEffect(() => {
    if (currentApp === 'poker' && hasActivePokerSession(user?.uid)) {
      setShowHeader(false);
    } else {
      setShowHeader(true);
    }
  }, [currentApp, user]);

  const isAwaitingAuthentication = loading || !user;

  const pokerAppLayer = !isAwaitingAuthentication && (isPrivateBuild() || currentApp === 'poker') ? (
    <div className={currentApp === 'poker'
      ? 'bg-zinc-950 text-zinc-200 h-[100dvh] flex flex-col overflow-hidden'
      : 'fixed inset-0 invisible pointer-events-none overflow-hidden'}>
      <Suspense fallback={null}>
        <PokerApp
          onHeaderVisibilityChange={setShowHeader}
          onInitialLoadComplete={() => setIsPokerStartupReady(true)}
          onBack={() => setCurrentApp('home')}
        />
      </Suspense>
    </div>
  ) : null;

  const appSwitchView = isAwaitingAuthentication ? (
    !loading ? <AuthScreen /> : null
  ) : currentApp === 'home' ? (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 overflow-hidden relative">
        {/* Background Ornaments */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-emerald-500/40 blur-[120px] rounded-full" />
          <div className="absolute top-2/3 -right-24 w-80 h-80 bg-rose-500/60 blur-[120px] rounded-full" />
          <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-full h-64 bg-zinc-900/50 blur-[60px]" />
        </div>

        <header className="relative z-10 p-6 flex justify-between items-center bg-zinc-950/50 backdrop-blur-md border-b border-zinc-900/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-12 h-12 rounded-2xl flex items-center justify-center bg-zinc-900 border border-zinc-800 shadow-[0_0_20px_rgba(244,63,94,0.2)]"
            >
              <Logo size={32} />
            </motion.div>
            <div>
              <motion.h1 
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="text-xl font-black text-white uppercase tracking-tighter leading-none"
              >
                AirChips
              </motion.h1>
              <motion.p 
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-[10px] text-zinc-500 font-bold uppercase tracking-[0.2em] mt-1"
              >
                Gaming Hub
              </motion.p>
            </div>
          </div>
          <button 
            onClick={() => setShowLogoutConfirm(true)} 
            className="p-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-400 hover:text-white hover:border-zinc-700 transition-all active:scale-90"
          >
            <LogOut size={18} />
          </button>
        </header>

        <main className="relative z-10 max-w-md mx-auto p-6 flex flex-col gap-5 mt-8">
          <div className="mb-2">
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-black uppercase tracking-[0.2em] text-zinc-600 mb-1"
            >
              Select Experience
            </motion.h2>
          </div>

          <motion.button
            initial={{ opacity: 0, y: 20}}
            animate={{ opacity: 1, y: 0}}
            transition={{ delay: 0.1, duration: 0.3 }}
            whileHover={{scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setCurrentApp('leaderboard')}
            className="w-full relative overflow-hidden flex flex-col items-start gap-4 p-8 rounded-[2.5rem] bg-zinc-900/40 border border-emerald-500/20 backdrop-blur-sm shadow-2xl hover:border-emerald-500/40 transition-all group"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-3xl group-hover:bg-emerald-500/10 transition-colors" />
            
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20 group-hover:bg-emerald-500 group-hover:text-black group-hover:border-emerald-400 transition-all duration-300">
              <Trophy size={28} />
            </div>
            
            <div className="text-left">
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter group-hover:text-emerald-400 transition-colors">Leaderboard</h2>
              <p className="text-sm text-zinc-500 mt-1 font-medium max-w-[200px]">Competitive ranking system for any card game.</p>
            </div>

            <div className="mt-2 flex gap-2">
               <span className="text-[9px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400 px-2 py-1 rounded-lg border border-zinc-700/50">Elo System</span>
               <span className="text-[9px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400 px-2 py-1 rounded-lg border border-zinc-700/50">Analytics</span>
            </div>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 20}}
            animate={{ opacity: 1, y: 0}}
            transition={{ delay: 0.2, duration: 0.3 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={openPokerApp}
            className="w-full relative overflow-hidden flex flex-col items-start gap-4 p-8 rounded-[2.5rem] bg-zinc-900/40 border border-rose-500/20 backdrop-blur-sm shadow-2xl hover:border-rose-500/40 transition-all group"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/5 blur-3xl group-hover:bg-rose-500/10 transition-colors" />
            
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500 border border-rose-500/20 group-hover:bg-rose-500 group-hover:text-black group-hover:border-rose-400 transition-all duration-300">
              <Spade size={28} />
            </div>
            
            <div className="text-left">
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter group-hover:text-rose-400 transition-colors">Poker Room</h2>
              <p className="text-sm text-zinc-500 mt-1 font-medium max-w-[200px]">Live poker session tracking and stack management.</p>
            </div>

            <div className="mt-2 flex gap-2">
               <span className="text-[9px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400 px-2 py-1 rounded-lg border border-zinc-700/50">Real-time</span>
               <span className="text-[9px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400 px-2 py-1 rounded-lg border border-zinc-700/50">Chip Stacks</span>
            </div>
          </motion.button>
        </main>
        
      </div>
  ) : currentApp === 'leaderboard' ? (
      <div className="bg-zinc-950 text-zinc-200 min-h-screen">
      {showHeader && import.meta.env.VITE_PUBLIC_OFFLINE !== 'true' && currentApp === 'leaderboard' && (
        <div className="sticky top-0">
          <ViewingHeader title="Leaderboard" onBack={() => setCurrentApp('home')} />
        </div>
      )}
      
      <AnimatePresence mode="wait">
        <motion.div
          key="leaderboard"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative z-0"
        >
          <Suspense fallback={<LoadingScreen variant="rose" />}>
            <LeagueProvider>
              <LeaderboardApp onHeaderVisibilityChange={setShowHeader} />
            </LeagueProvider>
          </Suspense>
        </motion.div>
      </AnimatePresence>
      </div>
  ) : <div className="hidden" aria-hidden="true" />;

  return (
    <>
      {appSwitchView}
      {pokerAppLayer}
      <LogoutConfirm 
        isOpen={showLogoutConfirm} 
        onConfirm={handleLogout} 
        onCancel={() => setShowLogoutConfirm(false)} 
      />
      <UpdatePrompt />
      <InstallPrompt />
      {startupLoadingScreen}
    </>
  );
}
